import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const SUPPORTED_LANGUAGES = ['pt-BR', 'en-US', 'es-ES'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const APP_LANGUAGE_KEY = 'app-language';
// Fonte única do idioma padrão — evita literais 'pt-BR' espalhados fora deste módulo.
export const DEFAULT_LANGUAGE: SupportedLanguage = 'pt-BR';

export function isSupportedLanguage(
  value: string | null | undefined
): value is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(value as SupportedLanguage);
}

// Cadeia da spec: escolha explícita persistida → idioma do navegador → pt-BR.
// O idioma do perfil entra depois do login, via UserPreferencesProvider
// (servidor vence no login, mesmo padrão do sidebarPreference).
//
// NÃO é chamada na init abaixo: o i18n inicializa no import deste módulo, antes de
// loadConfig() resolver, então a flag i18nEnabled ainda não é conhecida aqui. Quem chama é o
// bootstrap em main.tsx, depois da config e só com a flag ligada — ver o comentário do `lng`.
export function detectInitialLanguage(): SupportedLanguage {
  try {
    const stored = localStorage.getItem(APP_LANGUAGE_KEY);
    if (isSupportedLanguage(stored)) return stored;
  } catch {
    // storage indisponível — segue para o navegador
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language : undefined;
  if (nav) {
    const lower = nav.toLowerCase();
    if (lower.startsWith('pt')) return DEFAULT_LANGUAGE;
    if (lower.startsWith('en')) return 'en-US';
    if (lower.startsWith('es')) return 'es-ES';
  }
  return DEFAULT_LANGUAGE;
}

// Carrega todo JSON de locales/ automaticamente: lote novo = só criar o arquivo.
const modules = import.meta.glob<Record<string, unknown>>('./locales/*/*.json', {
  eager: true,
  import: 'default',
});

const resources: Record<string, Record<string, Record<string, unknown>>> = {};
for (const [path, content] of Object.entries(modules)) {
  const match = path.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match || !match[1] || !match[2]) continue;
  const lng = match[1];
  const ns = match[2];
  (resources[lng] ??= {})[ns] = content;
}

const namespaces = [...new Set(Object.values(resources).flatMap((byNs) => Object.keys(byNs)))];

void i18n.use(initReactI18next).init({
  resources,
  // pt-BR fixo na init, nunca detectInitialLanguage(): enquanto a flag i18nEnabled estiver
  // desligada o produto é só pt-BR, e detectar aqui vazaria a feature para quem tem o
  // navegador em outro idioma — sem seletor na tela para voltar. Com a flag ligada, o
  // bootstrap aplica a detecção via changeLanguage antes do primeiro render.
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...SUPPORTED_LANGUAGES],
  ns: namespaces,
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

export default i18n;
